<template>
    <v-container>
        <!-- :picker-date.sync="showPickerDate" -->
        <!-- <v-date-picker
             v-model="pickerDate"
             full-width
             ></v-date-picker> -->

        <v-card>
            <v-card-title>
                {{ accountTitle }}
                <br>
                {{ statusViewTitle }}
            </v-card-title>
            <v-card-subtitle
            >
                {{ formattedLastCheckUnixtime }}
            </v-card-subtitle>
        </v-card>
        <v-data-table
            :headers="headers"
            :items="statusItems"
            item-key="serial"
            :items-per-page="20" 
            :footer-props="{
                'items-per-page-options' : [20, 40, -1]
            }"
            dense
            class="elevation-5"
        >

            <!-- column: name -->
            <template v-slot:item.name="{ item }">
                <div v-if="! canEditNames">
                    <!-- Use a computed name to add the serial number -->
                    {{ formatName( item ) }}
                </div>
                <!-- Edit dialog for the sensor name -->
                <v-edit-dialog
                    large
                    persistent
                    @save="saveSensorName( item.sensorName )"
                    v-if="canEditNames"
                >
                    <!-- Use a computed name to add the serial number -->
                    {{ formatName( item ) }}
                    <template v-slot:input>
                        <div v-if="item.sensorName.ch2 != undefined" class="mt-4 title" >Sensor 1</div>
                        <v-text-field
                            v-model="item.sensorName.ch1"
                            autofocus
                        ></v-text-field>
                        <div v-if="item.sensorName.ch2 != undefined" class="mt-4 title" >Sensor 2</div>
                        <v-text-field
                            v-model="item.sensorName.ch2"
                            v-if="item.sensorName.ch2 != undefined"
                        ></v-text-field>
                    </template>
                </v-edit-dialog>
                <!-- tooltip showing the serial number -->
                <!-- 
                     <v-tooltip bottom>
                     <template v-slot:activator="{ on, attrs }">
                     <span v-bind="attrs" v-on="on" >
                     {{ item.name }}
                     </span>
                     </template>
                     <span> {{ item.serial }} </span>
                     </v-tooltip>
                -->
            </template>

        </v-data-table>
    </v-container>
</template>

<script>
import { API, Hub } from 'aws-amplify';
import { listLastSensorUnixTimes, listSensorNames } from '@/graphql/queries';
import { createSensorName, updateSensorName } from  '@/graphql/mutations';
import { text, currentLanguage } from '@/language';

const dayjs = require( 'dayjs' );
const utcPlugin = require( 'dayjs/plugin/utc' );
const relativeTimePlugin = require( 'dayjs/plugin/relativeTime' );

if ( currentLanguage() === 'es' )
    require( 'dayjs/locale/es' );

dayjs.extend( utcPlugin );
dayjs.extend( relativeTimePlugin );
dayjs.locale( currentLanguage() );

import {
    compareSensorsByName,
    pingAccount,
    dayjsFormatDate,
    dayjsFormatTime,
    peek,
    property,
    deviceSensorName,
} from '@/tools';

peek(); // lint

const DATE_FORMAT = 'YYYY-MM-DD h:mm a';

export default {
    name: 'StatusView',

    async created() {
        this.globalState = this.$root.$data.globalState;
        Hub.listen( 'monitor', this.hubListenCallback );
        this.computeCanEditNames();
    },

    mounted() {
        this.showAccount();
    },

    destroyed() {
        Hub.remove( 'monitor', this.hubListenCallback );
    },

    data: () => ({

        globalState : {},

        accountTitle : '',
        statusViewTitle : text( 'statusViewTitle' ),

        headers : [
            {
                text : 'Sensor',
                value : 'name',
            },
            {
                text : text( 'date' ),
                value : 'formattedDate',
            },
            {
                text : text( 'time' ),
                value : 'formattedTime',
            },
            {
                text : text( 'periodSinceLastReading' ),
                value : 'formattedLastUpdated',
            },
        ],
        statusItems : [],

        formattedLastCheckUnixtime : '?',

        accountDevicesCache : {
            loginId : '',
            devices : [],
        },

        canEditNames : false,
        sensorNameMap : {},
    }),

    computed : {
    },

    watch: {
    },

    methods : {
        hubListenCallback( data ) {
            // peek( 'hubListenCallback' )( { data } );
            if ( ! ['ev:setAccount', 'ev:userRole'].includes( data.payload.event ) )
                return;

            this.computeCanEditNames();
            this.showAccount();
        },

        computeCanEditNames() {
            this.canEditNames = this.globalState.userRole.isSuperUser;
        },

        formatName( statusItem ) {
            // peek( 'formatName')( statusItem );
            // WARNING: super users see a different thing than other users
            if ( ! this.canEditNames )
                return statusItem.name;

            const { sensorName } = statusItem;

            return `[${statusItem.serial}, ${statusItem.deviceName}] ${sensorName.ch1}` 
                 + ( sensorName.ch2 ? ', ' + sensorName.ch2 : '' );
        },

        async showAccount() {
            const account = this.globalState.account;

            if ( account ) 
                this.accountTitle = account.location + ': ' + account.description;
            else 
                this.accountTitle = '';

            this.getAccountDevices( account )
                .catch( error => {
                    console.log( 'WARNING: could not get devices', error );
                    return [];
                } )
                .then( devices =>
                    this.getSensorNameMap( devices.map( property( 'serial' ) ) ).then( sensorNameMap => {
                        this.sensorNameMap = sensorNameMap;
                        // peek( 'sensorNameMap' )( sensorNameMap );
                        return devices;
                    } )
                )
                .then( devices  => this.getLastSensorUnixTimes( account )
                                         .then( lastSensorUnixTimes => [ devices, lastSensorUnixTimes ] ) )
                .then( ( [devices, lastSensorUnixTimes] ) => {
                    // peek( '[devices, lastSensorUnixTimes]' )( { devices } );

                    const deviceSerials = devices.map( device => device.serial );

                    this.statusItems = lastSensorUnixTimes
                        .filter( last => account.loginId === last.tndAccountLoginId )
                        .filter( last => deviceSerials.includes( last.serial ) )
                        .sort( compareSensorsByName )
                        .map( last => {
                            const date = dayjs.unix( last.unixtime );
                            last.formattedUnixtime = date.format( DATE_FORMAT );
                            last.formattedDate = dayjsFormatDate( date );
                            last.formattedTime = dayjsFormatTime( date );
                            last.formattedLastCheckUnixtime = dayjs.unix( last.lastCheckUnixtime ).format( DATE_FORMAT );
                            last.formattedLastUpdated = date.fromNow();

                            // sensorName for the channel name dialog
                            const device = devices.find( device => device.serial === last.serial );
                            last.sensorName = deviceSensorName( this.sensorNameMap, device );
                            last.deviceName = device.name;

                            return last;
                        } );
                    // peek( 'statusItems' )( this.statusItems );
                    if ( this.statusItems.length > 0 )
                        this.formattedLastCheckUnixtime = 'Última actualización en ' + this.statusItems[0].formattedLastCheckUnixtime;
                    else
                        this.formattedLastCheckUnixtime = '';
                } );
        },

        saveSensorName( sensorName ) {
            peek( 'saveSensorName()')( sensorName );
            this.backendUpdateSensorName( sensorName )
                .then( () => this.sensorNameMap[ sensorName.serial ] = sensorName );
        },

        getAccountDevices( account ) {
            return pingAccount( this.globalState.accountDevicesCache, account );
        },

        ////////////////////////////////////////////////////////////////////////////////
        // Back end

        getLastSensorUnixTimes( account ) {
            if ( ! account ) {
                console.log( 'There is no account selected' );

                return Promise.resolve( [] );
            }

            const params = {
                query : listLastSensorUnixTimes,
                variables : {
                    limit : 1000,
                },
            };

            return API.graphql( params )
                      .then( result => result.data.listLastSensorUnixTimes.items )
                      .catch( error => {
                          console.log( 'Error calling listLastSensorUnixTimes(): ', error );
                          return [];
                      } );
        },

        getSensorNameMap( serials ) {
            const params = {
                query : listSensorNames,
                variables : {
                    limit : 2000,
                },
            };

            const toMap = ( map, sensorName ) => {
                map[ sensorName.serial ] = sensorName;

                return map;
            };

            return API.graphql( params )
                      .then( result => {
                          const items = result.data.listSensorNames.items;
                          // peek( 'sensorNameMap' )( { serials, items } );
                          // peek( 'filter')( items.filter( item => serials.includes( item.serial ) ).reduce( toMap, {} ) );
                          return items.filter( item => serials.includes( item.serial ) )
                                      .reduce( toMap, {} );
                      } )
                      .catch( error => {
                          console.log( 'Error calling listSensorNames(): ', error );
                          return {};
                      } );
        },

        backendUpdateSensorName( sensorName ) {
            const params = {
                query : updateSensorName,
                variables: { input: sensorName },
            };

            return API.graphql( params )
                      .then( result => result.data.updateSensorName )
                      .catch( error => {
                          console.log( `Could not update sensor name ${sensorName}, assuming we need to create a new one`, error );
                          return this.backendCreateSensorName( sensorName );
                      } );
        },

        backendCreateSensorName( sensorName ) {
            const params = {
                query : createSensorName,
                variables: { input: sensorName },
            };

            return API.graphql( params )
                      .then( result => result.data.updateSensorName )
                      .catch( error => console.log( 'Error creating sensor name', error ) );
        },
    },
}
</script>
